import { useRef, useEffect } from "react"
import { useMapView } from "./MapProvider"

/**
 * Attaches drag handling to a given feature.
 * NOTE: This will mutate the existing graphic passed in without causing a re-render.
 * If you want to re-render, pass in a callback to onDragEnd (2nd argument).
 * @param graphic The graphic to make draggable
 */
export const useDraggableGraphic = (
  graphic: __esri.Graphic | undefined,
  onDragEnd?: (graphic: __esri.Graphic) => void | undefined
) => {
  /** Attach dragging event */
  const draggingRef = useRef(false)
  const { view } = useMapView()
  useEffect(() => {
    if (graphic) {
      view?.when().then(() => {
        view.on("drag", e => {
          view.hitTest(e).then(res => {
            if (
              res.results &&
              res.results[0].graphic === graphic &&
              e.action === "start"
            ) {
              // on the first drag, set the dragging ref to if there's a match
              draggingRef.current = true
            } else if (e.action === "end") {
              // set the dragging ref to false to restore normal map dragging
              draggingRef.current = false
            }
          })

          // prevent map dragging if we're dragging and update the geometry
          if (draggingRef.current) {
            e.stopPropagation()
            graphic.geometry = view.toMap(e)
          }

          // finally, call the onDragEnd if it's there on the last event
          if (e.action === "end" && onDragEnd) {
            onDragEnd(graphic)
          }
        })
      })
      return () => {
        if (graphic && view) {
          view.graphics.remove(graphic)
        }
      }
    }
  }, [view, graphic, onDragEnd])
}
